<script>
  import { actions } from '../../utils/actions';
  import { getPath } from '../../utils/imageKit.ts';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  
  let {
    headingText,
    subheadingText,
    blurb,
    headerStyles = 'centered',
    promoTiles = $bindable(),
    displayInline = true,
    wide = true,
    dataTestId = 'steps-promo',
  } = $props();

  // Handle Image Setup
  promoTiles.forEach((step, index) => {
    if (step.image) {
      promoTiles[index].image.srcset = {};

      if (step.cropImage === false) {
        promoTiles[index].image.srcset.defaultSrc = getPath(step.image, { h: 220 });
        promoTiles[index].image.srcset.mobileDefaultSrc = getPath(step.image, { h: 220 });
        promoTiles[index].image.srcset.mobileHighResSrc = getPath(step.image, { h: 220 }, 2);
        promoTiles[index].image.srcset.tabletDefaultSrc = getPath(step.image, { h: 280 });
        promoTiles[index].image.srcset.tabletHighResSrc = getPath(step.image, { h: 280 }, 2);
        promoTiles[index].image.srcset.desktopDefaultSrc = getPath(step.image, { h: 360 });
        promoTiles[index].image.srcset.desktopHighResSrc = getPath(step.image, { h: 360 }, 2);
      } else {
        promoTiles[index].image.srcset.defaultSrc = getPath(step.image, { w: 424, h: 220 });
        promoTiles[index].image.srcset.mobileDefaultSrc = getPath(step.image, { w: 424, h: 220 });
        promoTiles[index].image.srcset.mobileHighResSrc = getPath(step.image, { w: 424, h: 220 }, 2);

        if (displayInline === true) {
          promoTiles[index].image.srcset.tabletDefaultSrc = getPath(step.image, { w: 320, h: 280 });
          promoTiles[index].image.srcset.tabletHighResSrc = getPath(step.image, { w: 320, h: 280 }, 2);
          promoTiles[index].image.srcset.desktopDefaultSrc = getPath(step.image, { w: 352, h: 360 });
          promoTiles[index].image.srcset.desktopHighResSrc = getPath(step.image, { w: 352, h: 360 }, 2);
        } else {
          promoTiles[index].image.srcset.tabletDefaultSrc = getPath(step.image, { w: 576, h: 280 });
          promoTiles[index].image.srcset.tabletHighResSrc = getPath(step.image, { w: 576, h: 280 }, 2);
          promoTiles[index].image.srcset.desktopDefaultSrc = getPath(step.image, { w: 672, h: 360 });
          promoTiles[index].image.srcset.desktopHighResSrc = getPath(step.image, { w: 672, h: 360 }, 2);
        }
      }
    }
  });
</script>

<div class="px-4 w-full mx-auto max-w-6xl my-6 md:my-10 lg:my-14" data-testid={dataTestId}>
  {#if headingText}
    <div
      class="max-w-4xl mx-auto w-full mb-6"
      class:text-center={headerStyles === 'centered'}
      class:max-w-6xl={headerStyles === 'full-width'}
      class:lg:mb-10={displayInline === false}
      class:lg:mb-12={displayInline === true}
    >
      <h2 class="font-semibold text-blue-dark-navy text-xl sm:text-1.5xl md:text-2xl lg:text-3xl">{headingText}</h2>
      {#if subheadingText}
        <p class="max-w-full md:text-lg xl:text-xl" class:text-lg={blurb} class:md:text-xl={blurb}>
          {subheadingText}
        </p>
      {/if}

      {#if blurb}
        <div class="mt-4 md:text-lg">
          {@html blurb}
        </div>
      {/if}
    </div>
  {/if}
  <ol
    class="items-stretch grid gap-6 w-full max-w-106 text-blue-navy md:gap-4"
    class:mx-auto={headerStyles !== 'full-width' ||
      (headerStyles === 'full-width' && displayInline === true && wide === false)}
    class:mb-4={!promoTiles[0].image}
    class:md:max-w-4xl={headerStyles !== 'centered' && displayInline === false && wide === true}
    class:md:max-w-2xl={headerStyles === 'centered' && displayInline === false}
    class:lg:max-w-4xl={(headerStyles === 'centered' && displayInline === false && wide === true) ||
      (displayInline === true && wide === false)}
    class:md:max-w-6xl={displayInline === true}
    class:md:grid-cols-3={displayInline === true}
    class:lg:gap-6={!promoTiles[0].image && displayInline === true}
    class:lg:gap-8={promoTiles[0].image || (displayInline === false && wide === true)}
    data-testid="{dataTestId}-bullet-list"
  >
    {#each promoTiles as step, index (index)}
      <li
        class="flex"
        class:shadow-y-sm={step.image}
        class:lg:shadow-y-lg={step.image}
        class:flex-cols={step.image}
        class:flex-col-reverse={step.image}
        class:text-center={step.image}
        class:md:flex-col={displayInline === true}
        class:lg:flex-row={!step.image && displayInline === true}
        class:text-left={!step.image}
        class:md:text-center={!step.image && displayInline === true}
        class:lg:text-left={!step.image && displayInline === true}
      >
        {#if !step.image && !step.numberStatistic}
          <span
            class="bg-blue-bright-cyan w-10 min-w-10 h-10 rounded-full flex justify-center items-center text-xl mt-1 mr-6 lg:mr-4 xl:h-12 xl:w-12 xl:min-w-12"
            class:md:mx-auto={displayInline === true}
            class:md:mb-4={displayInline === true}>{index + 1}</span
          >
        {/if}
        {#if step.link && step.link.url}
          <BaseLink
            {...step.link}
            className={step.image && 'p-4 text-center md:grow md:flex-col md:items-center lg:py-6 lg:px-8'}
            >{@html step.content}</BaseLink
          >
        {:else if step.action && step.action.event}
          <button
            class:self-start={!step.image}
            class:text-left={!step.image}
            class:md:text-center={!step.image && displayInline === true}
            class:lg:text-left={!step.image && displayInline === true}
            class:p-4={step.image}
            class:text-center={step.image}
            class:md:flex={step.image}
            class:md:grow={step.image}
            class:md:items-center={step.image}
            class:lg:py-6={step.image}
            class:lg:px-8={step.image}
            onclick={() => actions(step.action.event)}>{@html step.content}</button
          >
        {:else}
          <div
            class:p-4={step.image}
            class:justify-center={step.image}
            class:md:grow={step.image}
            class:md:flex={step.image}
            class:md:items-center={step.image}
            class:lg:py-6={step.image}
            class:lg:px-8={step.image}
            class:self-center={!step.image && displayInline === false}
            class:md:text-lg={displayInline === false}
            class:text-center={step.numberStatistic}
            class:max-w-72={step.numberStatistic}
            class:text-lg={step.numberStatistic}
            class:xl:text-xl={step.numberStatistic}
            class:mx-auto={step.numberStatistic}
          >
            {#if step.numberStatistic}
              <p class="text-blue-cerulean font-semibold text-5xl xl:text-6xl">{step.numberStatistic}</p>
            {/if}
            {@html step.content}
          </div>
        {/if}
        {#if step.image}
          <div class="w-full bg-blue-very-light h-55 md:h-70 lg:h-90">
            <picture>
              <source
                media="(min-width: 1024px)"
                srcset={`${step.image.srcset.desktopDefaultSrc}, ${step.image.srcset.desktopHighResSrc} 2x`}
              />
              <source
                media="(min-width: 640px)"
                srcset={`${step.image.srcset.tabletDefaultSrc}, ${step.image.srcset.tabletHighResSrc} 2x`}
              />
              <source
                media="(max-width: 639px)"
                srcset={`${step.image.srcset.mobileDefaultSrc}, ${step.image.srcset.mobileHighResSrc} 2x`}
              />
              <img
                class="mx-auto h-full"
                class:w-full={step.cropImage === true}
                class:object-cover={step.cropImage === true}
                class:lg:object-contain={step.cropImage === false}
                src={step.image.srcset.defaultSrc}
                alt={step.image.alt}
              />
            </picture>
          </div>
        {/if}
      </li>
    {/each}
  </ol>
</div>
